import { useState, useEffect } from "react";
import { Button, Modal } from "@legal-500/legal500-design-system";
import { campaignsService, type SegmentTarget } from "@/api/campaigns.service";
import LoadingState from "@/components/LoadingState";
import { recipientStatusColor, recipientStatusLabel } from "./recipient-status";
import styles from "./MailingGroups.module.css";

type Props = {
  campaignId: string;
  segmentId: string;
  label: string;
  onClose: () => void;
};

export function SegmentTargetsModal({ campaignId, segmentId, label, onClose }: Props) {
  const [targets, setTargets] = useState<SegmentTarget[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    campaignsService
      .listTargets(campaignId, segmentId)
      .then(res => {
        if (!cancelled) setTargets(res.targets);
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : "Failed to load targets.");
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, segmentId]);

  return (
    <Modal isOpen onClose={onClose} title={`Targets — ${label}`} size="medium" blurBackground>
      <div className={styles.modalForm}>
        {error && <p className={styles.mutedNote}>{error}</p>}
        {!targets && !error && <LoadingState message="Loading targets..." />}
        {targets && (
          <ul className={styles.targetsList}>
            {targets.length === 0 && <li className={styles.mutedNote}>No targets.</li>}
            {targets.map(t => (
              <li key={t.id} className={styles.targetRow}>
                <span className={styles.targetEmail}>{t.email}</span>
                <span className={styles.statusChip}>
                  <span
                    className={styles.statusDot}
                    style={{ backgroundColor: recipientStatusColor(t.status) }}
                    aria-hidden="true"
                  />
                  {recipientStatusLabel(t.status)}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className={styles.modalFooter}>
          <Button variant="outlined" color="default" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
}
